import React from 'react';

export type AuthInputFocus = {
  /** Attach to the email <input>. */
  emailRef: React.RefObject<HTMLInputElement>;
  /** Spread onto the email <input>: drives the readonly autofill guard. */
  inputProps: {
    onFocus: React.FocusEventHandler<HTMLInputElement>;
    onPointerDown: React.PointerEventHandler<HTMLInputElement>;
    onKeyDown: React.KeyboardEventHandler<HTMLInputElement>;
  };
};

/**
 * Autofocuses the auth email field on show, WITHOUT letting the browser's native
 * autofill / saved-email suggestion popup pop open on that programmatic focus —
 * while keeping the field focused and immediately typeable, and still surfacing
 * the popup on a genuine user click (so saved emails remain one tap away).
 *
 * The popup was triggering on the load-time `.focus()` and, because `/login` and
 * `/register` are separate routes that fully unmount each other, the open popup
 * got orphaned and floated in place across the switch (even over other apps).
 *
 * Technique ("readonly-until-focus", validated cross-browser): the field is made
 * `readonly` AT the focus event, so the engine places the caret but treats the
 * field as not-fillable and shows nothing; the guard is dropped on the first
 * genuine user engagement (pointerdown / keydown / a non-synthetic focus), after
 * which the field is an ordinary editable email input and a later click shows
 * suggestions normally. We deliberately keep `name`/`autoComplete="email"` intact
 * (no `autocomplete="off"`/garbled-name hacks) so legitimate autofill still works.
 *
 * Everything is imperative (attribute, not React state): re-rendering the readonly
 * flag risks a dropped first keystroke and a mid-focus re-pop on Chrome.
 */
export const useAuthInputFocus = (active: boolean): AuthInputFocus => {
  const emailRef = React.useRef<HTMLInputElement | null>(null);
  // True only across the synthetic programmatic focus below, so its own `onFocus`
  // does NOT strip the readonly guard (which would defeat the suppression).
  const programmaticRef = React.useRef<boolean>(false);

  React.useEffect(() => {
    if (!active) {
      return undefined;
    }
    const el = emailRef.current;
    if (el === null) {
      return undefined;
    }
    // Re-arm on EVERY programmatic focus (the form can re-show when loading/layout
    // flips): readonly at the focus event keeps the autofill popup shut, while the
    // caret is still placed so the user can start typing immediately.
    programmaticRef.current = true;
    el.setAttribute('readonly', 'readonly');
    el.focus({ preventScroll: true });
    // Clear the guard on the next macrotask — any later focus is user-originated.
    const clear = window.setTimeout(() => {
      programmaticRef.current = false;
    }, 0);
    return () => window.clearTimeout(clear);
  }, [active]);

  // Blur on unmount so an open native popup can't get orphaned across the
  // /login <-> /register route swap (each route fully unmounts the other). Must
  // run while the input is still mounted (effect cleanup does), and must be
  // imperative — Chrome often skips React's onBlur around autofill.
  React.useEffect(
    () => () => {
      const el = document.activeElement;
      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
        el.blur();
      }
    },
    [],
  );

  const enable = React.useCallback((): void => {
    // Imperative + synchronous within the event tick, so the field is editable
    // before the keystroke/popup decision (no dropped first key, no re-pop).
    emailRef.current?.removeAttribute('readonly');
  }, []);

  const onFocus = React.useCallback<React.FocusEventHandler<HTMLInputElement>>(() => {
    // Only a genuine (user-originated) focus makes the field editable; the
    // synthetic focus-on-show is skipped so its popup stays suppressed.
    if (!programmaticRef.current) {
      enable();
    }
  }, [enable]);

  const onPointerDown = React.useCallback<React.PointerEventHandler<HTMLInputElement>>(() => {
    // pointerdown fires before the click's focus/popup decision → editable in time.
    enable();
  }, [enable]);

  const onKeyDown = React.useCallback<React.KeyboardEventHandler<HTMLInputElement>>(() => {
    // Type-from-load / Tab-then-type: strip readonly before the keystroke lands.
    enable();
  }, [enable]);

  return { emailRef, inputProps: { onFocus, onPointerDown, onKeyDown } };
};
